import React, { useContext, useState } from 'react'

export interface contextProps {
    children: React.ReactNode
}

const AuthUserContext = React.createContext<any>('')
const AuthUserUpdateContext = React.createContext<any>('')
// Multi-org state — populated from `/init`. `primary` is the user's Cognito
// `custom:organisation` (drives the post-login landing page); `organisations`
// is the union of that primary plus any extras stored in
// `dashboard_user_org_memberships`. `activeOrganisation` is the org whose
// sidebar/view the user is currently looking at — it defaults to `primary`
// every login (no persistence) and only changes in-session via the sidebar
// org switcher.
const OrgsContext = React.createContext<{
    primary: string
    organisations: string[]
    activeOrganisation: string
}>({
    primary: '',
    organisations: [],
    activeOrganisation: ''
})
const SetOrgsContext = React.createContext<(orgs: { primary: string; organisations: string[] }) => void>(() => {})
const SetActiveOrganisationContext = React.createContext<(org: string) => void>(() => {})

// PCP Referral team-leader state — populated from `/init`. A team leader is an
// engagement-org user whose team membership is flagged `is_leader`; they get a
// read-only stats page scoped to their own team. `teamId`/`teamName` describe
// the team they lead (null when they are not a leader).
export interface TeamLeaderState {
    isLeader: boolean
    teamId: number | null
    teamName: string | null
}
const DEFAULT_TEAM_LEADER: TeamLeaderState = { isLeader: false, teamId: null, teamName: null }
const TeamLeaderContext = React.createContext<TeamLeaderState>(DEFAULT_TEAM_LEADER)
const SetTeamLeaderContext = React.createContext<(tl: TeamLeaderState) => void>(() => {})

export function useAuthUserData() {
    return useContext(AuthUserContext)
}

export function useUpdateAuthUserData() {
    return useContext(AuthUserUpdateContext)
}

export function useUserOrganisations() {
    return useContext(OrgsContext)
}

export function useSetUserOrganisations() {
    return useContext(SetOrgsContext)
}

export function useSetActiveOrganisation() {
    return useContext(SetActiveOrganisationContext)
}

// `useIsPcpReferralTeamLeader` — returns the current user's PCP Referral team
// leader state ({ isLeader, teamId, teamName }). Used to gate the Team Leader
// stats route + sidebar link.
export function useIsPcpReferralTeamLeader() {
    return useContext(TeamLeaderContext)
}

export function useSetPcpReferralTeamLeader() {
    return useContext(SetTeamLeaderContext)
}

// `canSee` — the single helper used by route gating and any feature flag that
// previously read `authUser['custom:organisation'] === 'X'`. Returns true if
// the org is anywhere in the user's granted set (primary or extras).
export function useCanSee() {
    const { organisations } = useUserOrganisations()
    return (org: string) => organisations.includes(org)
}

// `useIsSentinelUser` — true for internal Sentinel (admin) users, false for
// external introducer ("regular") logins. Used to gate admin-only UI such as
// the detailed Fundable Clients card. Mirrors the router's `canSeeWithFallback`
// pattern: prefer the granted org set, but fall back to the raw Cognito
// `custom:organisation` so the very first render after a hard refresh (before
// the orgs context rehydrates) still resolves correctly for Sentinel staff.
export function useIsSentinelUser() {
    const { organisations } = useUserOrganisations()
    const authUser = useContext(AuthUserContext)
    return organisations.includes('Sentinel') || authUser?.['custom:organisation'] === 'Sentinel'
}

export function AuthUserProvider({ children }: contextProps) {
    const [authUserData, setAuthUserData] = useState<any>()
    const [orgsState, setOrgsState] = useState<{
        primary: string
        organisations: string[]
        activeOrganisation: string
    }>({ primary: '', organisations: [], activeOrganisation: '' })
    const [teamLeaderState, setTeamLeaderState] = useState<TeamLeaderState>(DEFAULT_TEAM_LEADER)

    function updateAuthUser(au: any) {
        setAuthUserData(au)
    }

    function setTeamLeader(tl: TeamLeaderState) {
        setTeamLeaderState(tl || DEFAULT_TEAM_LEADER)
    }

    function setOrganisations({ primary, organisations }: { primary: string; organisations: string[] }) {
        // Active org is reset to the primary on every refresh — no persistence
        // across sessions. Users navigating between orgs mid-session use the
        // sidebar switcher and the choice lives only in React state.
        setOrgsState({ primary, organisations, activeOrganisation: primary })
    }

    function setActiveOrganisation(org: string) {
        setOrgsState((prev) => {
            if (!prev.organisations.includes(org)) return prev
            return { ...prev, activeOrganisation: org }
        })
    }

    return (
        <AuthUserContext.Provider value={authUserData}>
            <AuthUserUpdateContext.Provider value={updateAuthUser}>
                <OrgsContext.Provider value={orgsState}>
                    <SetOrgsContext.Provider value={setOrganisations}>
                        <SetActiveOrganisationContext.Provider value={setActiveOrganisation}>
                            <TeamLeaderContext.Provider value={teamLeaderState}>
                                <SetTeamLeaderContext.Provider value={setTeamLeader}>
                                    {children}
                                </SetTeamLeaderContext.Provider>
                            </TeamLeaderContext.Provider>
                        </SetActiveOrganisationContext.Provider>
                    </SetOrgsContext.Provider>
                </OrgsContext.Provider>
            </AuthUserUpdateContext.Provider>
        </AuthUserContext.Provider>
    )
}
